// Summary: Orders list table for POS with selection state and transfer-aware affordances.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-orderssection
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Menu, Modal, TextInput, UnstyledButton } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faChevronLeft, faEllipsisVertical, faMagnifyingGlass, faMotorcycle, faRotateLeft } from '@fortawesome/free-solid-svg-icons';
import { faFileLines } from '@fortawesome/free-regular-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { ItemActionsIcon } from '../../../../../components/ItemActionsIcon/ItemActionsIcon';
import type { OrderType, OrderView } from '../../../../../services/orders/orders.service';
import { subscribeSharedTicker } from '../../../../../utils/shared-ticker';
import { formatMoney, MONEY_DECIMALS } from '../../../../../utils/money';
import { isAggregatorOrderCode, isQuickChargeOrderCode, takeAwayChannelLabel } from 'shared-models/browser';
import { aggregatorOrderAlertTone } from '../aggregator-order.utils';
import classes from './orders-table.module.scss';

// An aggregator chip filters by its order-code prefix, so a new channel needs no new key.
type TypeFilterKey = OrderType | 'all' | 'quick_charge' | `channel-${string}`;

const aggregatorCodePrefix = (orderCode: string): string => `${orderCode.split('_')[0]}_`;

export type OrdersTableProps = {
    orders: OrderView[];
    selectedOrderId?: number | null;
    onSelectOrder: (order: OrderView) => void;
    filterSlot?: React.ReactNode;
    // History reuse: hide the dine-in/take-away pills, override the search hint + empty message.
    hideTypeFilter?: boolean;
    searchPlaceholder?: string;
    emptyMessage?: string;
    // While loading, show a loading row instead of the empty message (avoids paid-list flicker).
    isLoadingPaid?: boolean;
    // שולם toggle: independent 4th chip; the three type chips stay mutually exclusive.
    paidActive?: boolean;
    onTogglePaid?: () => void;
    // Per-row חשבון quick bill preview; parent owns the modal/fetch (Rule 32).
    onPreviewBill?: (orderId: number) => void;
    // Per-row קבלה receipt preview shown only in paid mode; parent owns the modal/fetch.
    onPreviewReceipt?: (orderId: number) => void;
    onRefundOrder?: (order: OrderView) => void;
    // Per-row פעולות פריט; parent opens the shared item-actions flow over the list.
    onItemActions?: (orderId: number) => void;
    // Per-row וולט lifecycle (WO_ rows only, active + paid lists); parent owns the modal.
    onWoltActions?: (orderId: number) => void;
    // Per-row תן ביס lifecycle (TB_ rows only, active + paid lists); parent owns the modal.
    onTenbisActions?: (orderId: number) => void;
    // Code prefixes of the channels enabled for this tenant; their chip shows even with no orders.
    enabledChannelPrefixes?: string[];
    // Revenue figure: the tips-inclusive footer total is manager-only.
    isManager?: boolean;
    isTransferTargetSelectionActive?: boolean;
};

type MobileRowActionProps = {
    icon: React.ReactNode;
    iconClass: string;
    isPrimary?: boolean;
    testId: string;
    title: string;
    subtitle: string;
    onClick: () => void;
};

const MobileRowAction: React.FC<MobileRowActionProps> = ({ icon, iconClass, isPrimary = false, testId, title, subtitle, onClick }) => (
    <UnstyledButton
        component="button"
        type="button"
        className={`${classes.mobileOrderAction} ${isPrimary ? classes.mobileOrderActionPrimary : ''}`}
        data-testid={testId}
        onClick={onClick}
    >
        <span className={`${classes.mobileOrderActionIcon} ${iconClass}`}>{icon}</span>
        <span className={classes.mobileOrderActionText}>
            <strong>{title}</strong>
            <small>{subtitle}</small>
        </span>
        <FontAwesomeIcon icon={faChevronLeft as IconProp} className={classes.mobileOrderActionChevron} />
    </UnstyledButton>
);

// Architectural summary: Orders table UI for the POS orders tab (details in function library docs).
const OrdersTableComponent: React.FC<OrdersTableProps> = ({
    orders,
    selectedOrderId = null,
    onSelectOrder,
    filterSlot,
    hideTypeFilter = false,
    searchPlaceholder = 'חיפוש לפי שולחן, מלצר או שם לקוח',
    emptyMessage = 'אין הזמנות פתוחות',
    isLoadingPaid = false,
    paidActive = false,
    onTogglePaid,
    onPreviewBill,
    onPreviewReceipt,
    onRefundOrder,
    onItemActions,
    onWoltActions,
    onTenbisActions,
    enabledChannelPrefixes,
    isManager = false,
    isTransferTargetSelectionActive = false,
}) => {
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState<TypeFilterKey>('all');
    const [now, setNow] = useState(() => Date.now());
    const [mobileActionOrder, setMobileActionOrder] = useState<OrderView | null>(null);
    const [descriptionOrderId, setDescriptionOrderId] = useState<number | null>(null);
    const descriptionPressRef = useRef<{ timer: ReturnType<typeof setTimeout>; x: number; y: number } | null>(null);
    const descriptionClickConsumedRef = useRef(false);
    const descriptionOrder = orders.find((order) => order.id === descriptionOrderId);
    const isMobile = useMediaQuery('(max-width: 767px)');

    // Architectural summary: Refreshes elapsed-time columns every 30s.
    useEffect(() => {
        return subscribeSharedTicker(30_000, () => setNow(Date.now()));
    }, []);

    useEffect(() => () => {
        if (descriptionPressRef.current) clearTimeout(descriptionPressRef.current.timer);
    }, []);

    const filteredOrders = useMemo(() => {
        const normalized = search.trim().toLowerCase();
        return orders.filter((order) => {
            const isQuickCharge = isQuickChargeOrderCode(order.orderCode);
            const isAggregator = isAggregatorOrderCode(order.orderCode);
            if (typeFilter.startsWith('channel-') && `channel-${aggregatorCodePrefix(order.orderCode)}` !== typeFilter) {
                return false;
            }
            if (typeFilter === 'quick_charge' && !isQuickCharge) {
                return false;
            }
            if (typeFilter === 'take_away' && (order.orderType !== 'take_away' || isQuickCharge || isAggregator)) {
                return false;
            }
            if (typeFilter === 'dine_in' && order.orderType !== 'dine_in') {
                return false;
            }
            if (!normalized) {
                return true;
            }
            const tableMatch = String(order.tableNumber ?? '').toLowerCase().includes(normalized);
            const codeMatch = order.orderCode.toLowerCase().includes(normalized);
            const guestMatch = (order.guestName?.trim() || order.tableDescription || '').toLowerCase().includes(normalized);
            const waiterMatch = (order.openedByName ?? '').toLowerCase().includes(normalized);
            return tableMatch || codeMatch || guestMatch || waiterMatch;
        });
    }, [orders, search, typeFilter]);

    // One chip per enabled channel, plus any channel present in the list; an empty chip stays.
    const enabledChannelKey = (enabledChannelPrefixes ?? []).join(',');
    const channelOptions = useMemo(() => {
        const byPrefix = new Map<string, string>();
        const register = (prefix: string, label: string | null) => {
            if (label) {
                byPrefix.set(prefix, label);
            }
        };
        enabledChannelKey.split(',').filter(Boolean).forEach((prefix) => register(prefix, takeAwayChannelLabel(prefix)));
        orders.forEach((order) => {
            if (isAggregatorOrderCode(order.orderCode)) {
                register(aggregatorCodePrefix(order.orderCode), takeAwayChannelLabel(order.orderCode));
            }
        });
        return [...byPrefix.entries()].map(([prefix, label]) => ({ key: `channel-${prefix}` as TypeFilterKey, label }));
    }, [orders, enabledChannelKey]);

    // A channel can be disabled while its chip is selected, so drop the dangling filter.
    useEffect(() => {
        if (typeFilter.startsWith('channel-') && !channelOptions.some((option) => option.key === typeFilter)) {
            setTypeFilter('all');
        }
    }, [channelOptions, typeFilter]);

    const typeOptions: Array<{ key: TypeFilterKey; label: string }> = [
        { key: 'all', label: 'הכל' },
        { key: 'dine_in', label: 'רגיל' },
        { key: 'take_away', label: 'לקחת' },
        { key: 'quick_charge', label: 'חיוב/זיכוי מהיר' },
        ...channelOptions,
    ];

    const totalDiners = useMemo(
        () => filteredOrders.reduce((sum, order) => sum + Math.max(0, Number(order.dinersCount) || 0), 0),
        [filteredOrders],
    );

    const totalIncludingTips = useMemo(
        () => filteredOrders.reduce((sum, order) => sum + (Number(order.totalAmount) || 0), 0),
        [filteredOrders],
    );

    const roundSummaryAmounts = filteredOrders.length === 0
        ? MONEY_DECIMALS === 0
        : filteredOrders.every((order) => order.roundAmounts === true);

    const formatMinutes = (iso: string) => {
        const minutes = Math.max(0, Math.floor((now - new Date(iso).getTime()) / 60000));
        if (minutes < 1) {
            return 'עכשיו';
        }
        if (minutes < 60) {
            return `${minutes} דק׳`;
        }
        const hours = Math.floor(minutes / 60);
        const remainder = minutes % 60;
        return `${hours}:${remainder.toString().padStart(2, '0')} שע׳`;
    };

    const formatAveragePerDiner = (order: OrderView) => {
        const diners = Math.max(1, Number(order.dinersCount) || 0);
        const billableAmount = 'billableAmount' in order ? order.billableAmount : undefined;
        return formatMoney((Number(billableAmount ?? order.totalAmount) || 0) / diners, order.roundAmounts ?? false);
    };

    const handleRowClick = (order: OrderView) => {
        if (isMobile && !isTransferTargetSelectionActive) {
            setMobileActionOrder(order);
            return;
        }
        onSelectOrder(order);
    };

    const cancelDescriptionPress = () => {
        if (descriptionPressRef.current) clearTimeout(descriptionPressRef.current.timer);
        descriptionPressRef.current = null;
    };

    const startDescriptionPress = (event: React.PointerEvent, orderId: number) => {
        if (event.button !== 0 || event.isPrimary === false) return;
        cancelDescriptionPress();
        descriptionClickConsumedRef.current = false;
        descriptionPressRef.current = {
            x: event.clientX,
            y: event.clientY,
            timer: setTimeout(() => {
                descriptionPressRef.current = null;
                descriptionClickConsumedRef.current = true;
                setDescriptionOrderId(orderId);
            }, 500),
        };
    };

    const moveDescriptionPress = (event: React.PointerEvent) => {
        const press = descriptionPressRef.current;
        if (press && (Math.abs(event.clientX - press.x) > 12 || Math.abs(event.clientY - press.y) > 12)) {
            cancelDescriptionPress();
        }
    };

    const handleOpenMobileOrder = () => {
        if (!mobileActionOrder) return;
        const order = mobileActionOrder;
        setMobileActionOrder(null);
        onSelectOrder(order);
    };

    const handlePreviewMobileBill = () => {
        if (!mobileActionOrder || !onPreviewBill) return;
        const orderId = mobileActionOrder.id;
        setMobileActionOrder(null);
        onPreviewBill(orderId);
    };

    const handleMobileItemActions = () => {
        if (!mobileActionOrder || !onItemActions) return;
        const orderId = mobileActionOrder.id;
        setMobileActionOrder(null);
        onItemActions(orderId);
    };

    const handlePreviewMobileReceipt = () => {
        if (!mobileActionOrder || !onPreviewReceipt) return;
        const orderId = mobileActionOrder.id;
        setMobileActionOrder(null);
        onPreviewReceipt(orderId);
    };

    const handleMobileWoltActions = () => {
        if (!mobileActionOrder || !onWoltActions) return;
        const orderId = mobileActionOrder.id;
        setMobileActionOrder(null);
        onWoltActions(orderId);
    };

    const handleRefundMobileOrder = () => {
        if (!mobileActionOrder || !onRefundOrder) return;
        const order = mobileActionOrder;
        setMobileActionOrder(null);
        onRefundOrder(order);
    };

    // Channel orders are settled and refunded on their channel, never at the POS.
    const canRefund = (order: OrderView) => paidActive && isManager && !!onRefundOrder && !isAggregatorOrderCode(order.orderCode);

    const handleMobileTenbisActions = () => {
        if (!mobileActionOrder || !onTenbisActions) return;
        const orderId = mobileActionOrder.id;
        setMobileActionOrder(null);
        onTenbisActions(orderId);
    };

    return (
        <div className={classes.container}>
            {!hideTypeFilter && (
                <div className={classes.typeBanner} role="group" aria-label="סוג הזמנה">
                    {typeOptions.map((option) => (
                        <Button
                            key={option.key}
                            unstyled
                            type="button"
                            data-testid={`orders-table-type-${option.key}`}
                            aria-pressed={typeFilter === option.key}
                            className={`${classes.typeTab} ${typeFilter === option.key ? classes.typeTabActive : ''}`}
                            onClick={() => setTypeFilter(option.key)}
                        >
                            {option.label}
                        </Button>
                    ))}
                    {onTogglePaid && (
                        <Button
                            unstyled
                            type="button"
                            aria-pressed={paidActive}
                            className={`${classes.typeTab} ${paidActive ? classes.typeTabPaid : ''}`}
                            data-testid="orders-table-paid-toggle"
                            onClick={onTogglePaid}
                        >
                            שולם
                        </Button>
                    )}
                </div>
            )}
            <div className={classes.searchRow}>
                <TextInput
                    value={search}
                    onChange={(event) => setSearch(event.currentTarget.value)}
                    placeholder={searchPlaceholder}
                    data-testid="orders-table-search"
                    classNames={{ input: classes.searchInput }}
                    leftSection={<FontAwesomeIcon icon={faMagnifyingGlass as IconProp} className={classes.muted} />}
                    radius="md"
                    w="100%"
                />
            </div>

            {filterSlot}

            <div className={classes.tableWrapper}>
                <div className={classes.mobileHeader} aria-hidden="true">
                    <div className={classes.mobileMetaHeader}>
                        <span>נפתח</span>
                        <span>ממוצע</span>
                        <span>מחיר</span>
                    </div>
                    <span className={classes.mobileTableHeader}>שולחן</span>
                    <div className={classes.mobileGuestHeader}>
                        <span>מלצר</span>
                        <span>סועדים</span>
                        <span>לקוח</span>
                    </div>
                </div>
                <table className={classes.table}>
                    <thead className={classes.thead}>
                        <tr>
                            <th>שולחן/מס׳ הזמנה</th>
                            <th>מלצר</th>
                            <th>סועדים</th>
                            <th>שם לקוח</th>
                            <th>סכום כולל</th>
                            <th>זמן פתיחה</th>
                            <th>ממוצע לסועד</th>
                            <th aria-hidden="true" />
                        </tr>
                    </thead>
                    <tbody>
                        {filteredOrders.map((order) => {
                            const isSelected = selectedOrderId === order.id;
                            const guestName = order.guestName?.trim();
                            const description = !guestName ? order.tableDescription?.trim() : null;
                            const alertTone = aggregatorOrderAlertTone(order);
                            return (
                                <tr
                                    key={order.id}
                                    className={`${classes.row} ${isSelected ? classes.rowSelected : ''} ${paidActive && !isMobile ? classes.rowStatic : ''} ${alertTone ? classes[alertTone === 'blue' ? 'rowChannelAlertBlue' : 'rowChannelAlertGreen'] : ''}`}
                                    data-testid={`orders-table-row-${order.id}`}
                                    onClick={paidActive && !isMobile ? undefined : () => handleRowClick(order)}
                                >
                                    <td className={classes.tableCell} data-label={order.orderType === 'take_away' ? 'מס׳ הזמנה' : 'שולחן'}>
                                        <span className={`${classes.tableNumber}${order.tableNumber == null ? ` ${classes.tableNumberCode}` : ''}`}>{order.orderCode.endsWith(' זיכוי') ? 'זיכוי' : order.tableNumber ?? order.orderCode}</span>
                                    </td>
                                    <td data-label="מלצר">{order.openedByName ?? '—'}</td>
                                    <td className={classes.dinersCell} data-label="סועדים">
                                        <img src="/diners.png" alt="" aria-hidden="true" />
                                        {order.dinersCount}
                                    </td>
                                    <td
                                        className={guestName || description ? undefined : classes.muted}
                                        data-label="שם לקוח"
                                    >
                                        {description ? (
                                            <UnstyledButton
                                                type="button"
                                                className={classes.guestDescription}
                                                data-testid={`orders-table-description-${order.id}`}
                                                aria-label={`הערת שולחן: ${description}`}
                                                onPointerDown={(event) => startDescriptionPress(event, order.id)}
                                                onPointerMove={moveDescriptionPress}
                                                onPointerUp={cancelDescriptionPress}
                                                onPointerCancel={cancelDescriptionPress}
                                                onPointerLeave={cancelDescriptionPress}
                                                onBlur={cancelDescriptionPress}
                                                onContextMenu={(event) => event.preventDefault()}
                                                onClick={(event) => {
                                                    if (descriptionClickConsumedRef.current) {
                                                        event.preventDefault();
                                                        event.stopPropagation();
                                                    }
                                                }}
                                                onKeyDown={(event) => {
                                                    if (event.key === 'Enter' || event.key === ' ') {
                                                        event.preventDefault();
                                                        event.stopPropagation();
                                                        cancelDescriptionPress();
                                                        descriptionClickConsumedRef.current = true;
                                                        setDescriptionOrderId(order.id);
                                                    }
                                                }}
                                            >
                                                {description}
                                            </UnstyledButton>
                                        ) : guestName || (order.orderType === 'take_away' ? order.orderCode : '—')}
                                    </td>
                                    <td data-label="סכום כולל" className={classes.amountCell}>{formatMoney(order.totalAmount ?? 0, order.roundAmounts ?? false)} ₪</td>
                                    <td className={classes.openedCell} data-label="נפתח">
                                                                            <span className={classes.openedFolder} aria-hidden="true">📂</span>
                                        <span>{formatMinutes(order.createdAt)}</span>
                                    </td>
                                    <td className={classes.averageCell} data-label="ממוצע לסועד">
                                        <span>₪ {formatAveragePerDiner(order)}</span>
                                    </td>
                                    <td
                                        className={classes.billActionCell}
                                        data-testid={`orders-table-actions-${order.id}`}
                                        onClick={(event) => event.stopPropagation()}
                                    >
                                        {(onPreviewBill || (onItemActions && order.hasSentLines) || (paidActive && onPreviewReceipt) || canRefund(order) || (onWoltActions && order.woltOrderId) || (onTenbisActions && order.tenbisOrderId)) && (
                                            <Menu position="bottom-end" withinPortal shadow="md">
                                                <Menu.Target>
                                                    <UnstyledButton
                                                        component="button"
                                                        type="button"
                                                        className={classes.rowActionsTrigger}
                                                        data-testid={`orders-table-actions-trigger-${order.id}`}
                                                        aria-label={`פעולות הזמנה ${order.tableNumber ?? order.orderCode}`}
                                                    >
                                                        <FontAwesomeIcon icon={faEllipsisVertical as IconProp} />
                                                    </UnstyledButton>
                                                </Menu.Target>
                                                <Menu.Dropdown className={classes.rowActionsMenu}>
                                                    {onPreviewBill && !paidActive && (
                                                        <Menu.Item
                                                            leftSection={<FontAwesomeIcon icon={faFileLines as IconProp} />}
                                                            data-testid={`orders-table-bill-preview-${order.id}`}
                                                            onClick={() => onPreviewBill(order.id)}
                                                        >
                                                            חשבון
                                                        </Menu.Item>
                                                    )}
                                                    {onItemActions && order.hasSentLines && !paidActive && (
                                                        <Menu.Item
                                                            leftSection={<ItemActionsIcon className={classes.actionIconImage} />}
                                                            data-testid={`orders-table-item-actions-${order.id}`}
                                                            onClick={() => onItemActions(order.id)}
                                                        >
                                                            פעולות פריט
                                                        </Menu.Item>
                                                    )}
                                                    {paidActive && onPreviewReceipt && (
                                                        <Menu.Item
                                                            leftSection={<FontAwesomeIcon icon={faFileLines as IconProp} />}
                                                            data-testid={`orders-table-receipt-preview-${order.id}`}
                                                            onClick={() => onPreviewReceipt(order.id)}
                                                        >
                                                            קבלה
                                                        </Menu.Item>
                                                    )}
                                                    {canRefund(order) && (
                                                        <Menu.Item
                                                            leftSection={<FontAwesomeIcon icon={faRotateLeft as IconProp} />}
                                                            data-testid={`orders-table-refund-${order.id}`}
                                                            onClick={() => onRefundOrder?.(order)}
                                                        >
                                                            זיכוי
                                                        </Menu.Item>
                                                    )}
                                                    {onWoltActions && order.woltOrderId && (
                                                        <Menu.Item
                                                            leftSection={<FontAwesomeIcon icon={faMotorcycle as IconProp} />}
                                                            data-testid={`orders-table-wolt-${order.id}`}
                                                            onClick={() => onWoltActions(order.id)}
                                                        >
                                                            וולט
                                                        </Menu.Item>
                                                    )}
                                                    {onTenbisActions && order.tenbisOrderId && (
                                                        <Menu.Item
                                                            leftSection={<FontAwesomeIcon icon={faMotorcycle as IconProp} />}
                                                            data-testid={`orders-table-tenbis-${order.id}`}
                                                            onClick={() => onTenbisActions(order.id)}
                                                        >
                                                            תן ביס
                                                        </Menu.Item>
                                                    )}
                                                </Menu.Dropdown>
                                            </Menu>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        {filteredOrders.length === 0 && (
                            <tr className={classes.emptyRow}>
                                <td colSpan={8} className={classes.empty}>
                                    {isLoadingPaid
                                        ? 'טוען...'
                                        : orders.length === 0 ? emptyMessage : 'לא נמצאו תוצאות'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <Modal
                opened={Boolean(descriptionOrder?.tableDescription?.trim())}
                onClose={() => setDescriptionOrderId(null)}
                title="הערת שולחן"
                centered
                size="sm"
                dir="rtl"
            >
                <div className={classes.fullDescription} data-testid="orders-table-full-description">
                    {descriptionOrder?.tableDescription}
                </div>
            </Modal>

            <Modal
                opened={mobileActionOrder !== null}
                onClose={() => setMobileActionOrder(null)}
                title={mobileActionOrder && (mobileActionOrder.orderCode.endsWith(' זיכוי') ? 'זיכוי' : mobileActionOrder.tableNumber === null ? `הזמנה ${mobileActionOrder.orderCode}` : `שולחן ${mobileActionOrder.tableNumber}`)}
                centered
                radius="md"
                size="sm"
                dir="rtl"
                classNames={{
                    content: classes.mobileOrderActionContent,
                    header: classes.mobileOrderActionHeader,
                    title: classes.mobileOrderActionTitle,
                    body: classes.mobileOrderActionBody,
                }}
                closeButtonProps={{ 'data-testid': 'orders-table-mobile-action-close' } as Record<string, string>}
            >
                {mobileActionOrder && (
                    <div className={classes.mobileOrderActionModal}>
                        <p className={classes.mobileOrderActionSummary}>
                            {mobileActionOrder.dinersCount} אורחים · {formatMoney(mobileActionOrder.totalAmount ?? 0, mobileActionOrder.roundAmounts ?? false)} ₪
                        </p>
                        <div className={classes.mobileOrderActionList}>
                            {onTenbisActions && mobileActionOrder.tenbisOrderId && (
                                <MobileRowAction
                                    icon={<FontAwesomeIcon icon={faMotorcycle as IconProp} />}
                                    iconClass={classes.mobileOrderActionIconAction}
                                    testId="orders-table-mobile-action-tenbis"
                                    title="תן ביס"
                                    subtitle="קבל או עדכן מצב משלוח"
                                    onClick={handleMobileTenbisActions}
                                />
                            )}
                            {onWoltActions && mobileActionOrder.woltOrderId && (
                                <MobileRowAction
                                    icon={<FontAwesomeIcon icon={faMotorcycle as IconProp} />}
                                    iconClass={classes.mobileOrderActionIconAction}
                                    testId="orders-table-mobile-action-wolt"
                                    title="וולט"
                                    subtitle="קבל, דחה או עדכן מצב משלוח"
                                    onClick={handleMobileWoltActions}
                                />
                            )}
                            {paidActive ? <>
                                {onPreviewReceipt && <MobileRowAction
                                    icon={<FontAwesomeIcon icon={faFileLines as IconProp} />}
                                    iconClass={classes.mobileOrderActionIconBill}
                                    isPrimary
                                    testId="orders-table-mobile-action-receipt"
                                    title="הדפס קבלה"
                                    subtitle="הצג והדפס קבלה"
                                    onClick={handlePreviewMobileReceipt}
                                />}
                                {canRefund(mobileActionOrder) && <MobileRowAction
                                    icon={<FontAwesomeIcon icon={faRotateLeft as IconProp} />}
                                    iconClass={classes.mobileOrderActionIconAction}
                                    testId="orders-table-mobile-action-refund"
                                    title="זיכוי"
                                    subtitle="בחר תשלומים לזיכוי"
                                    onClick={handleRefundMobileOrder}
                                />}
                            </> : (
                                <>
                                    <MobileRowAction
                                        icon={<FontAwesomeIcon icon={faArrowRight as IconProp} />}
                                        iconClass={classes.mobileOrderActionIconPrimary}
                                        isPrimary
                                        testId="orders-table-mobile-action-open"
                                        title="כניסה להזמנה"
                                        subtitle="פתח ועדכן את ההזמנה הנוכחית"
                                        onClick={handleOpenMobileOrder}
                                    />
                                    {onPreviewBill && (
                                        <MobileRowAction
                                            icon={<FontAwesomeIcon icon={faFileLines as IconProp} />}
                                            iconClass={classes.mobileOrderActionIconBill}
                                            testId="orders-table-mobile-action-bill"
                                            title="הצג חשבון"
                                            subtitle="הצג/הדפס חשבון"
                                            onClick={handlePreviewMobileBill}
                                        />
                                    )}
                                    {onItemActions && mobileActionOrder.hasSentLines && (
                                        <MobileRowAction
                                            icon={<ItemActionsIcon className={classes.actionIconImage} />}
                                            iconClass={classes.mobileOrderActionIconAction}
                                            testId="orders-table-mobile-action-item-actions"
                                            title="פעולות פריט"
                                            subtitle="הנחה, ביטול או על חשבון הבית"
                                            onClick={handleMobileItemActions}
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )}
            </Modal>

            <div className={classes.footerBar}>
                <div className={classes.footerDesktopSummary}>
                    <span data-testid="orders-summary-diners">מספר סועדים: {totalDiners}</span>
                    <span data-testid="orders-summary-count">סך הכל הזמנות: {filteredOrders.length}</span>
                    {isManager && (
                        <span data-testid="orders-summary-total">סה״כ (כולל טיפים): {formatMoney(totalIncludingTips, roundSummaryAmounts)} ₪</span>
                    )}
                </div>
                <div className={classes.footerMobileSummary}>
                    <div className={classes.footerSummaryRow}>
                        <span className={classes.footerSummaryLabel}>מספר סועדים:</span>
                        <span className={classes.footerSummaryValue}>{totalDiners}</span>
                    </div>
                    <div className={classes.footerSummaryRow}>
                        <span className={classes.footerSummaryLabel}>סך הכל הזמנות:</span>
                        <span className={classes.footerSummaryValue}>{filteredOrders.length}</span>
                    </div>
                    {isManager && (
                        <div className={classes.footerSummaryRow}>
                            <span className={classes.footerSummaryLabel}>סה״כ (כולל טיפים):</span>
                            <span className={classes.footerSummaryValue}>{formatMoney(totalIncludingTips, roundSummaryAmounts)} ₪</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export const OrdersTable = React.memo(OrdersTableComponent);
